import { useNavigate } from "@tanstack/react-router";
import {
  CornerDownLeftIcon,
  HouseIcon,
  ListIcon,
  LogOutIcon,
  MoonIcon,
  PlusIcon,
  SearchIcon,
  ShapesIcon,
  SunIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useSignOut } from "@/auth/AuthGate";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useEntitiesMeta } from "@/entities/api";
import { useQuickCreate } from "@/entities/quick-create";
import { useTheme } from "@/lib/theme";
import type { Workspace } from "./nav";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaces: Workspace[];
}

/** Global ⌘K / Ctrl+K hotkey. */
export function useCommandPaletteHotkey(onToggle: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onToggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onToggle]);
}

export function CommandPalette({ open, onOpenChange, workspaces }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { resolvedTheme, toggleTheme } = useTheme();
  const signOut = useSignOut();
  const [query, setQuery] = useState("");
  const { data: entities = [] } = useEntitiesMeta();
  const { openCreate } = useQuickCreate();
  const listable = entities.filter((e) => e.workspace && e.section);
  const creatable = entities.filter((e) => e.can_create);

  const run = (fn: () => void) => {
    onOpenChange(false);
    setQuery("");
    fn();
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Command palette" description="Jump to a page or run a command">
      <CommandInput placeholder="Search records, pages and commands…" value={query} onValueChange={setQuery} />
      <CommandList>
        <CommandEmpty>No matching pages or commands.</CommandEmpty>

        {query.trim().length > 1 && (
          <CommandGroup heading="Records">
            {/* Record search arrives with the API's full-text endpoint (plan §2). */}
            <CommandItem value={`search-records ${query}`} disabled>
              <SearchIcon />
              <span>
                Search records for “{query.trim()}”
              </span>
              <CommandShortcut>soon</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        )}

        <CommandGroup heading="Go to">
          <CommandItem value="home dashboard" onSelect={() => run(() => void navigate({ to: "/" }))}>
            <HouseIcon /> Home
          </CommandItem>
          {workspaces
            .filter((ws) => ws.id !== "home")
            .flatMap((ws) =>
              ws.sections.map((s) => {
                const Icon = ws.icon;
                return (
                  <CommandItem
                    key={`${ws.id}/${s.slug}`}
                    value={`${ws.label} ${s.label}`}
                    onSelect={() =>
                      run(
                        () =>
                          void navigate({ to: "/$workspace/$section", params: { workspace: ws.id, section: s.slug } }),
                      )
                    }
                  >
                    <Icon />
                    <span className="text-muted-foreground">{ws.label}</span>
                    <span className="text-muted-foreground/60">/</span>
                    <span>{s.label}</span>
                  </CommandItem>
                );
              }),
            )}
          {import.meta.env.DEV && (
            <CommandItem value="dev components gallery" onSelect={() => run(() => void navigate({ to: "/dev/components" }))}>
              <ShapesIcon /> Component gallery
              <CommandShortcut>dev</CommandShortcut>
            </CommandItem>
          )}
        </CommandGroup>

        {listable.length > 0 && (
          <CommandGroup heading="Record lists">
            {listable.map((e) => (
              <CommandItem
                key={`list-${e.name}`}
                value={`go to ${e.label_plural} list ${e.name}`}
                onSelect={() =>
                  run(
                    () =>
                      void navigate({
                        to: "/$workspace/$section",
                        params: { workspace: e.workspace ?? "", section: e.section ?? "" },
                      }),
                  )
                }
              >
                <ListIcon /> Go to {e.label_plural}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {creatable.length > 0 && (
          <CommandGroup heading="Create">
            {creatable.map((e) => (
              <CommandItem key={`new-${e.name}`} value={`new create ${e.label} ${e.name}`} onSelect={() => run(() => openCreate(e.name))}>
                <PlusIcon /> New {e.label.toLowerCase()}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />
        <CommandGroup heading="Commands">
          <CommandItem value="toggle theme dark light" onSelect={() => run(toggleTheme)}>
            {resolvedTheme === "dark" ? <SunIcon /> : <MoonIcon />}
            Switch to {resolvedTheme === "dark" ? "light" : "dark"} theme
          </CommandItem>
          <CommandItem value="sign out log out" onSelect={() => run(signOut)}>
            <LogOutIcon /> Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <div className="flex items-center gap-3 border-t px-3 py-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <CornerDownLeftIcon className="size-3" /> to select
        </span>
        <span>↑↓ to navigate</span>
        <span>esc to close</span>
      </div>
    </CommandDialog>
  );
}
